import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { useManufacturerPerformance } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { ManufacturerPerformanceFilters } from '../services/reportsService';

interface ManufacturerPerformanceSectionProps {
  filters: ManufacturerPerformanceFilters;
}

type SortField = 'manufacturer_name' | 'frames_ordered' | 'frames_received' | 'avg_days';
type SortDirection = 'asc' | 'desc';

export function ManufacturerPerformanceSection({
  filters,
}: ManufacturerPerformanceSectionProps) {
  const { data, isLoading, error } = useManufacturerPerformance(filters);
  const [sortField, setSortField] = useState<SortField>('frames_ordered');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = data
    ? [...data].sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'manufacturer_name':
            comparison = a.manufacturer_name.localeCompare(b.manufacturer_name);
            break;
          case 'frames_ordered':
            comparison = a.frames_ordered - b.frames_ordered;
            break;
          case 'frames_received':
            comparison = a.frames_received - b.frames_received;
            break;
          case 'avg_days':
            comparison = a.avg_days_to_receive - b.avg_days_to_receive;
            break;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      })
    : [];

  const handleExport = () => {
    if (!sortedData) return;

    exportToCSV(
      sortedData.map((item) => ({
        Manufacturer: item.manufacturer_name,
        Location: item.location_name,
        'Frames Ordered': item.frames_ordered,
        'Frames Received': item.frames_received,
        'Avg Days to Receive': item.avg_days_to_receive,
      })),
      'manufacturer-performance-report'
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Manufacturer Performance</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Manufacturer Performance</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  if (!sortedData || sortedData.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Manufacturer Performance</h2>
        <div className="text-center py-8 text-gray-500">
          No data found for the selected filters
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Manufacturer Performance</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing data from {formatDate(filters.date_from)} to {formatDate(filters.date_to)}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('manufacturer_name')}
              >
                <div className="flex items-center gap-1">
                  Manufacturer
                  {sortField === 'manufacturer_name' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('frames_ordered')}
              >
                <div className="flex items-center justify-end gap-1">
                  Ordered
                  {sortField === 'frames_ordered' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('frames_received')}
              >
                <div className="flex items-center justify-end gap-1">
                  Received
                  {sortField === 'frames_received' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('avg_days')}
              >
                <div className="flex items-center justify-end gap-1">
                  Avg Days
                  {sortField === 'avg_days' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fulfillment %
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((item, index) => {
              const fulfillmentRate =
                item.frames_ordered > 0
                  ? ((item.frames_received / item.frames_ordered) * 100).toFixed(1)
                  : '0.0';

              return (
                <tr key={`${item.manufacturer_id}-${item.location_id}-${index}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    {item.manufacturer_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    <span className="hidden lg:inline">{item.location_name}</span>
                    <span className="lg:hidden">{item.location_short_code || item.location_name}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600 font-medium">
                    {item.frames_ordered}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-green-600 font-medium">
                    {item.frames_received}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {item.avg_days_to_receive > 0 ? item.avg_days_to_receive : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span
                      className={
                        parseFloat(fulfillmentRate) >= 90
                          ? 'text-green-600 font-medium'
                          : parseFloat(fulfillmentRate) >= 70
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }
                    >
                      {fulfillmentRate}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-500">
        Showing {sortedData.length} manufacturer-location combinations
      </div>
    </Card>
  );
}
